// src/pages/analytics/YoYOverviewTab.tsx
//
// Overview (YoY) — multi-year year-over-year matrix (recipe:
// sales-yoy-matrix). Dimension select + bucket pills (week/month/quarter/
// year) + calendar toggle (Gregorian | Hebrew) + years-back select. The
// default calendar comes from the tenant config key
// `analytics.default_calendar` (fallback 'gregorian'); a user's toggle
// override wins for the session.
//
// Period labels + year strings are server-formatted (Hebrew years like
// '5785') — rendered as-is. Cells show the value with a small line underneath
// chosen by the "%" switch (PBI "Select % Calculation"): change vs prior year,
// GP margin (▲▼ = margin points vs prior year), or share of the period column.
// Null cells (no data / no prior) render a muted "–". Margin/share need agent
// 3.20.0+. The filter pane (analytics.filterpane) narrows every number.
// Drill-down (flag analytics.drill): a row expands into its items / customers
// (./drill); child cells are mapped onto the parent's period columns by key.
// With the filter pane on, "Bucket" also offers Bi-Week / Bi-Month / Half
// (PBI "Arrange by") and a YTD toggle cuts every year at today's point of the
// year (PBI "YTD = True"). Agent 3.20.0+.

import { Fragment, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CalendarRange, RefreshCw, Download, AlertTriangle, FileSpreadsheet, RotateCcw, ChevronRight, ChevronDown } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import { useAuthContext } from '../../hooks/useAuth';
import { adminApi } from '../../services/adminApi';
import { apiV2 as api } from '../../services/apiV2';
import type { YoyCell, YoyRow, YoyMatrixResponse, YoyPeriod } from '../../types';
import { exportCsv, type CsvColumns } from '../../lib/csvExport';
import { exportXlsx } from '../../lib/xlsxExport';
import { FilterPane, AnalyticsSavedViews } from './filterPane';
import { useFilterPane } from './filterPaneState';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import { DRILL, DRILL_ROWS, canDrillLabel, drillParams } from './drill';

const fmtCur = (v: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
const fmtNum = (v: number) => new Intl.NumberFormat('en-US').format(Math.round(v));

const MEASURES: { value: string; label: string }[] = [
  { value: 'dollars', label: 'Dollars' },
  { value: 'qty',     label: 'Qty' },
];

const DIMENSIONS: { value: string; label: string }[] = [
  { value: 'price_level',       label: 'Price Level' },
  { value: 'salesman',          label: 'Salesman' },
  { value: 'category',          label: 'Category' },
  { value: 'area',              label: 'Area' },
  { value: 'customer_category', label: 'Customer Category' },
  { value: 'item_category',     label: 'Item Category' },
  { value: 'item_subcategory',  label: 'Item Subcategory' },
];

// pane: only offered with the filter pane on (agent 3.20.0+).
const BUCKETS: { value: string; label: string; pane?: boolean }[] = [
  { value: 'week',    label: 'Week' },
  { value: 'biweek',  label: 'Bi-Week',  pane: true },
  { value: 'month',   label: 'Month' },
  { value: 'bimonth', label: 'Bi-Month', pane: true },
  { value: 'quarter', label: 'Quarter' },
  { value: 'half',    label: 'Half',     pane: true },
  { value: 'year',    label: 'Year' },
];

const CALENDARS: { value: string; label: string }[] = [
  { value: 'gregorian', label: 'Gregorian' },
  { value: 'hebrew',    label: 'Hebrew' },
];

const YEARS_BACK = [2, 4, 6, 10];

type PctMode = 'yoy' | 'margin' | 'share';
const PCT_MODES: { value: PctMode; label: string; title: string }[] = [
  { value: 'yoy',    label: '% YoY',      title: 'Change vs the same period last year' },
  { value: 'margin', label: '% Margin',   title: 'Gross margin (▲▼ = margin points vs last year)' },
  { value: 'share',  label: '% of Total', title: 'Share of the period total' },
];

// Stable color per year index — oldest first, so the current year keeps the
// same hue as the user widens yearsBack.
const YEAR_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#a855f7', '#ec4899', '#14b8a6', '#f97316', '#eab308', '#06b6d4', '#64748b'];

function matrixCols(data: YoyMatrixResponse): CsvColumns<YoyRow> {
  return [
    [data.dimension, (r) => r.label],
    ...data.periods.map((p, i): [string, (r: YoyRow) => string | number] =>
      [p.label, (r) => r.cells[i]?.v ?? '']),
    ['Total',       (r) => r.total],
    ['Prev Total',  (r) => r.prevTotal],
    ['Total Δ%',    (r) => r.totalDeltaPct],
    ['Margin %',    (r) => r.totalMarginPct ?? ''],
    ['% of Total',  (r) => r.totalSharePct ?? ''],
  ];
}
const exportMatrix     = (data: YoyMatrixResponse) => exportCsv('sales-yoy-matrix', matrixCols(data), [...data.rows, data.totalRow]);
const exportMatrixXlsx = (data: YoyMatrixResponse) => exportXlsx('sales-yoy-matrix', matrixCols(data), [...data.rows, data.totalRow]);

function CellValue({ cell, fmt, mode = 'yoy' }: { cell: YoyCell | null; fmt: (v: number) => string; mode?: PctMode }) {
  if (!cell) return <span className="text-slate-600">–</span>;
  let sub: ReactNode;
  if (mode === 'margin') {
    const m = cell.marginPct;
    const pm = cell.prevMarginPct;
    const diff = m != null && pm != null ? m - pm : null;
    sub = m == null
      ? <span className="text-[10px] text-slate-600">–</span>
      : (
        <span className="text-[10px] tabular-nums text-slate-300">
          {m.toFixed(1)}%
          {diff != null && (
            <span className={diff >= 0 ? 'text-emerald-400' : 'text-red-400'}> {diff >= 0 ? '▲' : '▼'}{Math.abs(diff).toFixed(1)}</span>
          )}
        </span>
      );
  } else if (mode === 'share') {
    sub = cell.sharePct == null
      ? <span className="text-[10px] text-slate-600">–</span>
      : <span className="text-[10px] tabular-nums text-sky-300">{cell.sharePct.toFixed(1)}%</span>;
  } else {
    const up = cell.deltaPct >= 0;
    sub = (
      <span className={`text-[10px] tabular-nums ${up ? 'text-emerald-400' : 'text-red-400'}`}>
        {up ? '▲' : '▼'} {Math.abs(cell.deltaPct).toFixed(1)}%
      </span>
    );
  }
  return (
    <div className="flex flex-col items-end">
      <span className="text-slate-200 tabular-nums">{fmt(cell.v)}</span>
      {sub}
    </div>
  );
}

/** The row's Total column as a cell. */
const totalCell = (r: YoyRow): YoyCell => ({
  v: r.total, prev: r.prevTotal, deltaPct: r.totalDeltaPct,
  marginPct: r.totalMarginPct, prevMarginPct: r.prevTotalMarginPct, sharePct: r.totalSharePct,
});

/** Child rows under an expanded YoY row, aligned to the parent's periods. */
function YoyDrillRows({ dimension, label, periods, query, paneParams, paneKey, fmtVal, pctMode }: {
  dimension: string; label: string; periods: YoyPeriod[];
  query: { bucket: string; calendar: string; yearsBack: number; measure: string; ytd: boolean };
  paneParams: Record<string, string | undefined>; paneKey: string;
  fmtVal: (v: number) => string; pctMode: PctMode;
}) {
  const d = DRILL[dimension];
  const q = useQuery({
    queryKey: ['analyticsYoy', 'drill', dimension, label, query, paneKey],
    queryFn: () => api.getYoyMatrix({ dimension: d.child, ...query }, drillParams(paneParams, d, label)),
    staleTime: 60_000,
    retry: 1,
  });
  const span = periods.length + 2;
  if (q.isLoading || q.error) {
    return (
      <tr className="border-b border-slate-800/50 bg-slate-900/60">
        <td colSpan={span} className="pl-9 pr-3 py-2 text-slate-500 italic">
          {q.isLoading ? `Loading ${d.childLabel}…` : `Couldn't load ${d.childLabel}: ${(q.error as Error).message}`}
        </td>
      </tr>
    );
  }
  // A filtered child can have fewer periods than the parent — map by key.
  const childIdx = new Map((q.data?.periods ?? []).map((p, i) => [p.key, i]));
  const kids = q.data?.rows ?? [];   // server orders by current-year total
  return (
    <>
      {kids.slice(0, DRILL_ROWS).map((r, i) => (
        <tr key={`drill-${r.label}-${i}`} className="group border-b border-slate-800/40 bg-slate-900/60 hover:bg-slate-700/20">
          <td className="sticky left-0 z-10 bg-slate-950 group-hover:bg-slate-800 pl-9 pr-3 py-1.5 text-slate-300 whitespace-nowrap max-w-[240px] truncate" title={r.label}>{r.label}</td>
          {periods.map((p) => {
            const ci = childIdx.get(p.key);
            return (
              <td key={p.key} className="px-3 py-1.5 text-right align-top">
                <CellValue cell={ci != null ? r.cells[ci] ?? null : null} fmt={fmtVal} mode={pctMode} />
              </td>
            );
          })}
          <td className="px-3 py-1.5 text-right border-l border-slate-800/60">
            <CellValue cell={totalCell(r)} fmt={fmtVal} mode={pctMode} />
          </td>
        </tr>
      ))}
      {kids.length === 0 && (
        <tr className="border-b border-slate-800/50 bg-slate-900/60">
          <td colSpan={span} className="pl-9 pr-3 py-2 text-slate-500 italic">No {d.childLabel}</td>
        </tr>
      )}
      {kids.length > DRILL_ROWS && (
        <tr className="border-b border-slate-800/50 bg-slate-900/60">
          <td colSpan={span} className="pl-9 pr-3 py-1.5 text-[11px] text-slate-500">
            Top {DRILL_ROWS} of {kids.length} {d.childLabel}
          </td>
        </tr>
      )}
    </>
  );
}

export function YoYOverviewTab() {
  const { claims, activeTenant } = useAuthContext();
  const slug = activeTenant?.slug ?? claims?.tenant_slug ?? null;

  const [dimension, setDimension] = useState('category');
  // Expanded row label (drill-down); cleared when the dimension changes.
  const [expanded, setExpanded] = useState<string | null>(null);
  const [bucket, setBucket] = useState('month');
  const [yearsBack, setYearsBack] = useState(4);
  const [measure, setMeasure] = useState('dollars');
  const [ytd, setYtd] = useState(false);
  // null = "no user choice yet" — fall through to the tenant config default.
  const [calendarOverride, setCalendarOverride] = useState<string | null>(null);
  const [pctMode, setPctMode] = useState<PctMode>('yoy');
  const pane = useFilterPane();
  // YTD is a pane feature; with the pane off it's always the full year.
  const ytdOn = pane.enabled && ytd;
  const isQty = measure === 'qty';
  const fmtVal = isQty ? fmtNum : fmtCur;

  // Reset every control to its initial default.
  const resetFilters = () => {
    setDimension('category');
    setExpanded(null);
    setBucket('month');
    setYearsBack(4);
    setMeasure('dollars');
    setCalendarOverride(null);
    setPctMode('yoy');
    setYtd(false);
  };

  const calendarDefaultQ = useQuery({
    queryKey: ['analyticsYoy', 'default-calendar', slug],
    queryFn: () => adminApi.getConfig('analytics.default_calendar', slug ?? undefined),
    enabled: !!slug,
    staleTime: 60 * 60 * 1000,
    retry: 0,
    refetchOnWindowFocus: false,
  });
  const configCalendar = calendarDefaultQ.data?.value === 'hebrew' ? 'hebrew' : 'gregorian';
  const calendar = calendarOverride ?? configCalendar;

  const q = useQuery({
    queryKey: ['analyticsYoy', 'matrix', dimension, bucket, calendar, yearsBack, measure, ytdOn, pane.key],
    queryFn: () => api.getYoyMatrix({ dimension, bucket, calendar, yearsBack, measure, ytd: ytdOn }, pane.params),
    // Wait for the tenant default so we don't fetch gregorian and then
    // immediately refetch hebrew on tenants configured for it.
    enabled: !slug || calendarOverride !== null || !calendarDefaultQ.isLoading,
    staleTime: 60_000,
    retry: 1,
  });

  const data = q.data;
  const periods = data?.periods ?? [];
  const rows = data?.rows ?? [];
  const { canSee } = useFeatureAccess();
  // filtersActive marks an agent that honours the pane slicers (3.20.0+).
  const drill = canSee('analytics.drill') && data?.filtersActive != null ? DRILL[dimension] : undefined;

  // Reshape yearSeries into recharts grouped-bar rows: one entry per period,
  // one key per year. Year labels are strings ('2026' or '5785') — as-is.
  const chartData = periods.map((p, i) => {
    const entry: Record<string, string | number | null> = { label: p.label };
    (data?.yearSeries ?? []).forEach((ys) => { entry[ys.year] = ys.values[i] ?? null; });
    return entry;
  });

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-white flex items-center gap-2">
          <CalendarRange size={20} /> Overview (YoY)
        </h2>
        <p className="text-sm text-slate-400 mt-0.5">
          Year-over-year sales by period — current year vs the same period in prior years
          {data && <span className="text-slate-500"> · current year {data.currentYear} · {data.calendar} calendar{data.ytd ? ' · year to date' : ''}</span>}
        </p>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-end gap-3 p-4 rounded-xl bg-slate-800/20 border border-slate-700/30">
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Dimension</span>
          <select value={dimension} onChange={e => { setDimension(e.target.value); setExpanded(null); }}
            className="px-2 py-1.5 rounded-md bg-slate-800/50 border border-slate-700/50 text-xs text-white focus:outline-none focus:ring-1 focus:ring-blue-500/50">
            {DIMENSIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Bucket</span>
          <div className="flex items-center gap-1.5">
            {BUCKETS.filter(b => !b.pane || pane.enabled).map(b => (
              <button key={b.value} onClick={() => setBucket(b.value)}
                className={`px-2 py-1.5 text-[11px] rounded-md border transition-all ${
                  bucket === b.value
                    ? 'bg-blue-500/15 text-blue-200 border-blue-500/40'
                    : 'border-slate-700/50 text-slate-500 hover:text-slate-300 bg-slate-900/40'
                }`}>
                {b.label}
              </button>
            ))}
          </div>
        </div>
        {pane.enabled && (
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">YTD</span>
            <button onClick={() => setYtd(v => !v)}
              title="Cut every year at today's point of the year, so a part-way year compares like-for-like"
              className={`px-2 py-1.5 text-[11px] rounded-md border transition-all ${
                ytd
                  ? 'bg-blue-500/15 text-blue-200 border-blue-500/40'
                  : 'border-slate-700/50 text-slate-500 hover:text-slate-300 bg-slate-900/40'
              }`}>
              {ytd ? 'Year to date' : 'Full year'}
            </button>
          </div>
        )}
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Calendar</span>
          <div className="flex items-center gap-1.5">
            {CALENDARS.map(c => (
              <button key={c.value} onClick={() => setCalendarOverride(c.value)}
                className={`px-2 py-1.5 text-[11px] rounded-md border transition-all ${
                  calendar === c.value
                    ? 'bg-blue-500/15 text-blue-200 border-blue-500/40'
                    : 'border-slate-700/50 text-slate-500 hover:text-slate-300 bg-slate-900/40'
                }`}>
                {c.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Years</span>
          <select value={yearsBack} onChange={e => setYearsBack(Number(e.target.value))}
            className="px-2 py-1.5 rounded-md bg-slate-800/50 border border-slate-700/50 text-xs text-white focus:outline-none focus:ring-1 focus:ring-blue-500/50">
            {YEARS_BACK.map(y => <option key={y} value={y}>{y} years</option>)}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Measure</span>
          <div className="flex items-center gap-1.5">
            {MEASURES.map(m => (
              <button key={m.value} onClick={() => setMeasure(m.value)}
                className={`px-2 py-1.5 text-[11px] rounded-md border transition-all ${
                  measure === m.value
                    ? 'bg-blue-500/15 text-blue-200 border-blue-500/40'
                    : 'border-slate-700/50 text-slate-500 hover:text-slate-300 bg-slate-900/40'
                }`}>
                {m.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">%</span>
          <div className="flex items-center gap-1.5">
            {PCT_MODES.map(m => (
              <button key={m.value} onClick={() => setPctMode(m.value)} title={m.title}
                className={`px-2 py-1.5 text-[11px] rounded-md border transition-all ${
                  pctMode === m.value
                    ? 'bg-blue-500/15 text-blue-200 border-blue-500/40'
                    : 'border-slate-700/50 text-slate-500 hover:text-slate-300 bg-slate-900/40'
                }`}>
                {m.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex-1" />
        <AnalyticsSavedViews
          page="sales-yoy"
          getState={() => ({ dimension, bucket, calendar, yearsBack, measure, pctMode, ytd })}
          applyState={(s) => {
            if (typeof s.dimension === 'string' && DIMENSIONS.some(d => d.value === s.dimension)) { setDimension(s.dimension); setExpanded(null); }
            if (typeof s.bucket === 'string' && BUCKETS.some(b => b.value === s.bucket)) setBucket(s.bucket);
            if (typeof s.calendar === 'string' && CALENDARS.some(c => c.value === s.calendar)) setCalendarOverride(s.calendar);
            if (typeof s.yearsBack === 'number' && YEARS_BACK.includes(s.yearsBack)) setYearsBack(s.yearsBack);
            if (typeof s.measure === 'string' && MEASURES.some(m => m.value === s.measure)) setMeasure(s.measure);
            setYtd(s.ytd === true);
            if (typeof s.pctMode === 'string' && PCT_MODES.some(m => m.value === s.pctMode)) setPctMode(s.pctMode as PctMode);
          }}
        />
        <button
          onClick={resetFilters}
          title="Reset all filters to defaults"
          className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1">
          <RotateCcw size={11} /> Reset
        </button>
        <button
          onClick={() => data && exportMatrix(data)}
          disabled={!data || rows.length === 0}
          title="Export the YoY matrix to CSV"
          className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1 disabled:opacity-40">
          <Download size={11} /> CSV
        </button>
        <button
          onClick={() => data && exportMatrixXlsx(data)}
          disabled={!data || rows.length === 0}
          title="Export the YoY matrix to Excel"
          className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1 disabled:opacity-40">
          <FileSpreadsheet size={11} /> Excel
        </button>
        <button onClick={() => q.refetch()} disabled={q.isLoading}
          className="p-2 rounded-md bg-slate-700/50 border border-slate-600/50 text-slate-400 hover:text-white transition-all disabled:opacity-50">
          <RefreshCw size={14} className={q.isFetching ? 'animate-spin' : ''} />
        </button>
      </div>

      <FilterPane />

      {q.error && (
        <div className="p-4 rounded-md bg-red-950/30 border border-red-900/40 text-red-200 text-sm flex items-center gap-2">
          <AlertTriangle size={16} />
          <div>
            <div className="font-medium">Couldn't load the YoY matrix</div>
            <div className="text-xs text-red-300/70 mt-0.5">{(q.error as Error).message}</div>
          </div>
        </div>
      )}

      {q.isLoading || (!q.data && !q.error) ? (
        <div className="flex items-center justify-center h-40"><RefreshCw className="w-6 h-6 text-blue-400 animate-spin" /></div>
      ) : !q.error && data && (
        <>
          {/* Matrix table */}
          <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-slate-700/50 bg-slate-800/50">
                    <th className="sticky left-0 z-10 bg-slate-800 px-3 py-2.5 text-left text-slate-400 font-medium min-w-[160px]">
                      {DIMENSIONS.find(d => d.value === dimension)?.label ?? data.dimension}
                    </th>
                    {periods.map((p) => (
                      <th key={p.key} className="px-3 py-2.5 text-right text-slate-400 font-medium whitespace-nowrap">{p.label}</th>
                    ))}
                    <th className="px-3 py-2.5 text-right text-slate-400 font-medium border-l border-slate-700/40">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => {
                    const canExpand = !!drill && canDrillLabel(r.label);
                    const isOpen = canExpand && expanded === r.label;
                    return (
                      <Fragment key={`${r.label}-${i}`}>
                        <tr
                          onClick={canExpand ? () => setExpanded(isOpen ? null : r.label) : undefined}
                          title={canExpand ? `${isOpen ? 'Collapse' : 'Show'} ${drill!.childLabel}` : undefined}
                          className={`group border-b border-slate-800/50 hover:bg-slate-700/20 transition-colors${canExpand ? ' cursor-pointer' : ''}`}>
                          <td className="sticky left-0 z-10 bg-slate-900 group-hover:bg-slate-800 px-3 py-2 text-white whitespace-nowrap max-w-[240px] truncate" title={r.label}>
                            {drill && (
                              <span className="inline-block w-4 text-slate-500 align-middle">
                                {canExpand && (isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />)}
                              </span>
                            )}
                            {r.label}
                          </td>
                          {r.cells.map((c, ci) => (
                            <td key={ci} className="px-3 py-1.5 text-right align-top"><CellValue cell={c} fmt={fmtVal} mode={pctMode} /></td>
                          ))}
                          <td className="px-3 py-1.5 text-right border-l border-slate-800/60">
                            <CellValue cell={totalCell(r)} fmt={fmtVal} mode={pctMode} />
                          </td>
                        </tr>
                        {isOpen && (
                          <YoyDrillRows dimension={dimension} label={r.label} periods={periods}
                            query={{ bucket, calendar, yearsBack, measure, ytd: ytdOn }}
                            paneParams={pane.params} paneKey={pane.key} fmtVal={fmtVal} pctMode={pctMode} />
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
                {rows.length > 0 && (
                  <tfoot>
                    <tr className="border-t-2 border-slate-600/60 bg-slate-800/60 font-bold">
                      <td className="sticky left-0 z-10 bg-slate-800 px-3 py-2 text-white">Total</td>
                      {data.totalRow.cells.map((c, ci) => (
                        <td key={ci} className="px-3 py-1.5 text-right align-top"><CellValue cell={c} fmt={fmtVal} mode={pctMode} /></td>
                      ))}
                      <td className="px-3 py-1.5 text-right border-l border-slate-700/60">
                        <CellValue cell={totalCell(data.totalRow)} fmt={fmtVal} mode={pctMode} />
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
            {rows.length === 0 && (
              <div className="text-center py-8 text-slate-500 text-sm">No rows to show</div>
            )}
          </div>

          {/* Grouped bars — one series per year across the periods */}
          <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 p-4">
            <h3 className="text-sm font-semibold text-white mb-3">{isQty ? 'Qty' : 'Sales $'} by {data.bucket} — year over year</h3>
            {chartData.length === 0 ? (
              <div className="text-center py-8 text-slate-500 text-sm">No periods to chart</div>
            ) : (
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
                    <CartesianGrid vertical={false} stroke="#334155" strokeOpacity={0.4} />
                    <XAxis dataKey="label" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={{ stroke: '#475569' }} tickLine={false}
                      interval="preserveStartEnd" minTickGap={16} />
                    <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={{ stroke: '#475569' }} tickLine={false}
                      tickFormatter={(v) => isQty ? fmtNum(Number(v)) : `$${(Number(v) / 1000).toFixed(0)}k`} />
                    <Tooltip
                      cursor={{ fill: '#475569', fillOpacity: 0.15 }}
                      contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px' }}
                      labelStyle={{ color: '#f1f5f9' }}
                      formatter={(value, name) => [isQty ? fmtNum(Number(value)) : fmtCur(Number(value)), String(name)]}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }}
                      formatter={(v) => <span style={{ color: '#94a3b8' }}>{v}</span>} />
                    {(data.yearSeries ?? []).map((ys, i) => (
                      <Bar key={ys.year} dataKey={ys.year} fill={YEAR_COLORS[i % YEAR_COLORS.length]} radius={[3, 3, 0, 0]} maxBarSize={28} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
